// src/pages/TimeSeries/index.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Common Components
import BreadCrumb from "../../Components/Common/BreadCrumb";
import GeneralSettings from "../../Components/Common/GeneralSettings";
import PlotContainer from "../../Components/Common/PlotContainer";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";

// Page-specific Components
import TimeSeriesGroupsManager from "./components/TimeSeriesGroupsManager";

// API
import {
  getTimeSeriesData,
  generateTimeSeriesPlot,
  fetchJobStatus,
} from "../../helpers/eirna_backend_helper";

// Hooks
import { useSettings } from "../../hooks/useSettings";
import { useFileSelection } from "../../hooks/useFileSelection";
import { useJobPolling } from "../../hooks/useJobPolling";
import { useTimeSeriesGroups } from "./hooks/useTimeSeriesGroups";
import { EnterButtonListener } from "../../hooks/EnterButtonListener";
import { useScrollToRef } from "../../hooks/useScrollToRef";

// Short code handling
import {
  useTimeSeriesShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";

// Config
import {
  timeSeriesConfig,
  timeSeriesDefaults,
  timeSeriesValidation,
} from "../../config/plotConfigs/timeSeriesConfig";
import ActionButton from "../../Components/Common/ActionButton";
import InfoTooltip from "../../Components/Common/InfoTooltip";

const TimeSeries = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [loading, setLoading] = useState(true);
  const [plotData, setPlotData] = useState(null);
  const [plotBundle, setPlotBundle] = useState({});
  const [activePlot, setActivePlot] = useState('TE_plot');
  const [error, setError] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);

  // References to plotContainer and loadingDiv, to allow for auto scrolling
  const loadingDivRef  = useRef(null);
  const plotContainerRef  = useRef(null);

  const [apiData, setApiData] = useState({
    json_files: [],
    organism: "",
    transcriptome: "",
    default_tran: "",
  });

  // Hooks
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = useSettings(timeSeriesDefaults, timeSeriesValidation);

  const {
    selectedSeqType,
    selectedGroup,
    setSelectedSeqType,
    setSelectedGroup,
  } = useFileSelection("riboseq");

  const { plotLoading, taskId, startJob } = useJobPolling();

  // Time series groups manager
  const {
    timepoints,
    currentMaxVisibleTimepoint,
    addTimepoint,
    removeTimepoint,
    updateTimepointName,
    addFilesToTimepoint,
    removeFilesFromTimepoint,
    clearTimepoint,
    clearAllTimepoints,
    validateTimepoints,
    getMasterFileDict,
    setTimepoints,
    setCurrentMaxVisibleTimepoint,
  } = useTimeSeriesGroups();

  // Use the TimeSeries specific short code handler
  const { autoRunPlot, 
          setAutoRunPlot,
          consumeUserPassedShortCode } =
    useTimeSeriesShortCode({
      location,
      updateMultipleSettings,
      setTimepoints,
      setCurrentMaxVisibleTimepoint,
    });

  // A callback used by the EnterButtonListener to generate the plot
  const runPlot = useCallback(() => {
    if (plotLoading) return;
    setAutoRunPlot(true);
  }, [plotLoading]);

  // Listens for the Enter button being pressed, if so calls runPlot (as long as
  // a textarea is not in focus)
  EnterButtonListener({
    onEnter: runPlot,
    modalOpen: chooserOpen,
    enabled: true,
  });

  // Scroll to loadingDiv anytime plotLoading changes
  useScrollToRef(loadingDivRef, plotLoading);

  // Scroll to plot/table anytime plotBundle changes
  useScrollToRef(plotContainerRef, plotBundle);

  // Load data on component mount
  useEffect(() => {
    if (!projectId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const response = await getTimeSeriesData(projectId);
        const data = response?.data || response || {};

        setApiData({
          json_files: data.json_files || [],
          organism: data.organism || "",
          transcriptome: data.transcriptome || "",
          default_tran: data.default_tran || "",
        });

        // Set default gene list if available
        if (data.default_tran) {
          updateSetting("customGeneList", data.default_tran);
        }

        // Set default seq type
        const fileTypes = data.json_files
          ? [...new Set(data.json_files.map((x) => x.file_type))]
          : [];
        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }

        // Set default group
        const uniqueGroups = [
          ...new Set(
            data.json_files?.map((file) => file.subgroup).filter(Boolean) || []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }

        toast.success("Project data loaded successfully");
      } catch (err) {
        console.error("Failed to load project data:", err);
        const errorMsg = "Failed to load project data";
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, updateSetting, setSelectedSeqType, setSelectedGroup]);

  // Auto-trigger analysis generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot) {
      console.log("Auto-run triggered, checking conditions...", {
        autoRunPlot,
        plotLoading,
        loading,
        timepointsWithFiles: timepoints.filter(
          (tp) =>
            tp.controlRiboFiles.length > 0 ||
            tp.controlRnaFiles.length > 0 ||
            tp.treatRiboFiles.length > 0 ||
            tp.treatRnaFiles.length > 0
        ).length,
      });

      const hasFiles = timepoints.some(
        (tp) =>
          tp.controlRiboFiles.length > 0 ||
          tp.controlRnaFiles.length > 0 ||
          tp.treatRiboFiles.length > 0 ||
          tp.treatRnaFiles.length > 0
      );

      if (!plotLoading && !loading && hasFiles) {
        console.log(
          "All conditions met, generating TimeSeries analysis from short code..."
        );
        setAutoRunPlot(false);
        setTimeout(() => {
          handleGenerate();
        }, 50);
      } else {
        console.log(
          "Conditions not met yet, will retry when conditions change"
        );
      }
    }
  }, [
    autoRunPlot,
    plotLoading,
    loading,
    timepoints
      .map(
        (tp) =>
          tp.controlRiboFiles.length +
          tp.controlRnaFiles.length +
          tp.treatRiboFiles.length +
          tp.treatRnaFiles.length
      )
      .join(","),
    setAutoRunPlot,
  ]);

  // Computed values
  const filteredFiles = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    let files = apiData.json_files;
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((x) => x.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((x) => x.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  const seqTypes = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    const types = [...new Set(apiData.json_files.map((x) => x.file_type))];
    return types.map((type) => ({
      value: type,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      count: apiData.json_files.filter((x) => x.file_type === type).length,
    }));
  }, [apiData.json_files]);

  const availableGroups = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    const groupSet = new Set(
      apiData.json_files.map((x) => x.subgroup).filter(Boolean)
    );
    return Array.from(groupSet).map((group) => ({
      value: group,
      label: group,
      count: apiData.json_files.filter((x) => x.subgroup === group).length,
    }));
  }, [apiData.json_files]);

  // Event handlers
  const handleSettingUpdate = useCallback(
    (key, value) => {
      updateSetting(key, value);
    },
    [updateSetting]
  );

  const handleReset = useCallback(() => {
    resetSettings();
    clearAllTimepoints();
    setPlotData(null);
    setError(null);
    toast.info("Settings reset to defaults");
  }, [resetSettings, clearAllTimepoints]);

  // Build payload for API
  const buildPayload = useCallback(() => {
    const shortCodeForThisRequest = consumeUserPassedShortCode();
    // Use the TimeSeries specific payload creator
    return createPlotSpecificPayload.timeSeries({
      settings,
      projectId,
      userPassedShortCode: shortCodeForThisRequest,
      getMasterFileDict,
    });
  }, [settings, projectId, getMasterFileDict, consumeUserPassedShortCode]);

  // Generate analysis
  const handleGenerate = useCallback(async () => {
    if (plotLoading) {
      console.log("Job already in progress");
      return;
    }

    const validation = validateSettings();
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    const timepointValidation = validateTimepoints();
    if (!timepointValidation.isValid) {
      toast.error(timepointValidation.errors[0]);
      return;
    }

    const payload = buildPayload();
    console.log("Submitting TimeSeries payload:", payload);

    const onSuccess = (result) => {
      // Handle multiple plot types (TE, RIBO, RNA)
      const plotResult = {
        TE_plot: result.TE_plot || null,
        RIBO_plot: result.RIBO_plot || null,
        RNA_plot: result.RNA_plot || null,
      };
      setPlotBundle(plotResult);
      // Select the relevant plot button depending on what's in the result
      if (result.TE_plot) {
        setActivePlot('TE_plot')
      } else if (result.RIBO_plot) {
        setActivePlot('RIBO_plot')
      } else {
        setActivePlot('RNA_plot')
      };
      setError(null);
    };

    const onError = (errorMsg) => {
      setError(errorMsg);
      setPlotData(null);
    };

    startJob(generateTimeSeriesPlot, payload, onSuccess, onError);
  }, [
    plotLoading,
    validateSettings,
    validateTimepoints,
    buildPayload,
    startJob,
  ]);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#2F9E37" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = "Time Series Analysis | EIRNA Bio Connect";

  const totalFilesAssigned = timepoints.reduce(
    (sum, tp) =>
      sum +
      tp.controlRiboFiles.length +
      tp.controlRnaFiles.length +
      tp.treatRiboFiles.length +
      tp.treatRnaFiles.length,
    0
  );

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Time Series Analysis" },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <h4
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0",
                  }}
                >
                  Time Series Analysis
                </h4>
                <div>
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={handleReset}
                    disabled={plotLoading}
                  >
                    <i className="ri-refresh-line me-1"></i>
                    Reset All
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Settings Row */}
          <Row className="mb-4">
            {/* Left: General Settings */}
            <Col lg={12}>
              <GeneralSettings
                config={timeSeriesConfig}
                settings={settings}
                onUpdateSetting={handleSettingUpdate}
                onReset={handleReset}
                title="Time Series Settings"
                analysisMode={settings.analysisType}
                onSetAnalysisMode={(val) => handleSettingUpdate("analysisType", val)}
                analysisModeOptions={[
                  { value: "single_control", label: "Single Control" },
                  { value: "multi_control", label: "Multi Control" },
                ]}
              />
            </Col>
          </Row>
          <Row className="mb-4">
            {/* Right: Seq Types and Groups */}
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={seqTypes}
                groups={availableGroups}
                selectedSeqType={selectedSeqType}
                selectedGroup={selectedGroup}
                onSelectSeqType={setSelectedSeqType}
                onSelectGroup={setSelectedGroup}
                showSelectAllCheckboxes={false}
                customTitle="Seq Types & Groups"
                customIcon="ri-tools-fill"
              />
            </Col>
          </Row>

          {/* Time Series Groups Management */}
          <Row className="mb-4">
            <Col lg={12}>
              <TimeSeriesGroupsManager
                files={filteredFiles}
                allFiles={apiData.json_files}
                timepoints={timepoints}
                settings={settings}
                onUpdateSetting={handleSettingUpdate}
                onAddTimepoint={addTimepoint}
                onRemoveTimepoint={removeTimepoint}
                onUpdateTimepointName={updateTimepointName}
                onAddFilesToTimepoint={addFilesToTimepoint}
                onRemoveFilesFromTimepoint={removeFilesFromTimepoint}
                onClearTimepoint={clearTimepoint}
                onClearAll={clearAllTimepoints}
                currentMaxVisible={currentMaxVisibleTimepoint}
                maxTimepoints={12}
              />
            </Col>
          </Row>

          {/* Status and Generate Button */}
          <Row className="mb-4">
            <Col lg={12}>
              <div ref={loadingDivRef} 
                   className="d-flex justify-content-between align-items-center">
                <div className="text-muted">
                  <i className="ri-time-line me-1"></i>
                  Active Timepoints:{" "}
                  <strong>{currentMaxVisibleTimepoint + 1}</strong> | Total
                  Files Assigned: <strong>{totalFilesAssigned}</strong>
                  {totalFilesAssigned === 0 ? (
                    <span className="text-warning ms-2">
                      Please assign files to at least one timepoint
                    </span>
                  ) : (
                    <i className="ri-check-circle-line ms-2 text-success"></i>
                  )}
                  {taskId && (
                    <span className="ms-2">
                      - <strong>Task ID:</strong> {taskId}
                    </span>
                  )}
                </div>
                <ActionButton
                  onClick={handleGenerate}
                  disabled={plotLoading || totalFilesAssigned === 0}
                  loading={plotLoading}
                  buttonType="plot"
                />{" "}
              </div>
            </Col>
          </Row>


          {/* Results Container */}
          {(plotBundle || plotLoading || error) && (
          <React.Fragment>
            {plotBundle && (
              <Row className="mb-3">
                <Col lg={12} className="d-flex gap-2">
                  <button
                    type="button"
                    className={`btn btn-outline-primary ${activePlot === 'TE_plot' ? 'active' : ''}`}
                    onClick={() => setActivePlot('TE_plot')}
                    disabled={!plotBundle.TE_plot}
                  >
                    TE plot
                  </button>
                  <button
                    type="button"
                    className={`btn btn-outline-primary ${activePlot === 'RIBO_plot' ? 'active' : ''}`}
                    onClick={() => setActivePlot('RIBO_plot')}
                    disabled={!plotBundle.RIBO_plot}
                  >
                    RIBO plot
                  </button>
                  <button
                    type="button"
                    className={`btn btn-outline-primary ${activePlot === 'RNA_plot' ? 'active' : ''}`}
                    onClick={() => setActivePlot('RNA_plot')}
                    disabled={!plotBundle.RNA_plot}
                  >
                    RNA plot
                  </button>
                </Col>
              </Row>
            )}

            <Row>
              <Col lg={12}>
                <div style={{ display: activePlot === 'TE_plot' ? 'block' : 'none' }}>
                  <PlotContainer
                    ref={plotContainerRef}
                    key="TE_plot"
                    plotData={plotBundle.TE_plot || null}
                    loading={plotLoading}
                    error={error}
                    selectedFilesCount={totalFilesAssigned}
                    title="TE plot"
                    onRetry={handleGenerate}
                    showFileCount={true}
                  />
                </div>

                <div style={{ display: activePlot === 'RIBO_plot' ? 'block' : 'none' }}>
                  <PlotContainer
                    key="RIBO_plot"
                    plotData={plotBundle.RIBO_plot || null}
                    loading={plotLoading}
                    error={error}
                    selectedFilesCount={totalFilesAssigned}
                    title="RIBO plot"
                    onRetry={handleGenerate}
                    showFileCount={true}
                  />
                </div>

                <div style={{ display: activePlot === 'RNA_plot' ? 'block' : 'none' }}>
                  <PlotContainer
                    key="RNA_plot"
                    plotData={plotBundle.RNA_plot || null}
                    loading={plotLoading}
                    error={error}
                    selectedFilesCount={totalFilesAssigned}
                    title="RNA plot"
                    onRetry={handleGenerate}
                    showFileCount={true}
                  />
                </div>
              </Col>
            </Row>
          </React.Fragment>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TimeSeries;
